/**
 * `ReactionWaitBanner` — the **attacker/source-controller waiting-state** for a halted resolution.
 * When this client's action halts mid-resolution for someone else's reaction — a mech's Brace, a
 * third party's Valiant Aid, any cross-actor decision the server routes elsewhere — this transient
 * banner names the actual decider, derived from the live `game_decision` map by {@link
 * deriveResolutionWait} rather than guessed at from the target's kind.
 *
 * Unlike {@link RejectionToast} it carries **no auto-dismiss timer**: the wait is a real in-flight
 * state, not a fading notice — it renders for exactly as long as `deriveResolutionWait` still
 * finds a matching outstanding decision (which clears itself the moment the server's
 * `game_decision_resolved` removes the entry from the map). `key={wait.promptId}` replays the
 * enter animation whenever the derivation lands on a different halted decision.
 *
 * The queue-depth line renders ONLY when `wait.outstanding > 1` — a single outstanding wait shows
 * no depth chip and no layout change.
 */
import type { ResolutionWait } from "./resolutionWait";

export function ReactionWaitBanner({ wait }: { wait: ResolutionWait | null }) {
  if (!wait) return null;
  return (
    <div key={wait.promptId} className="combat-reactwait" role="status" aria-live="polite">
      <span className="combat-reactwait-spinner" aria-hidden="true" />
      <div className="combat-reactwait-text">
        <span className="combat-reactwait-eyebrow">OMNINET ⁄⁄ Reaction window</span>
        <span className="combat-reactwait-reason">
          Waiting for <b>{wait.deciderName}</b> to react…
        </span>
        {wait.outstanding > 1 && (
          <span className="combat-reactwait-queue">+{wait.outstanding - 1} more decision{wait.outstanding - 1 === 1 ? "" : "s"} outstanding</span>
        )}
      </div>
    </div>
  );
}
